import type * as React from "react";
import { useEffect, useRef } from "react";
import { Delete, Undo2 } from "lucide-react";

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import { cn } from "@/lib/utils";

type InputProps<
  T extends
    React.ComponentProps<"input">["type"] = React.ComponentProps<"input">["type"],
> = Omit<React.ComponentProps<"input">, "type"> & {
  type?: T;
  icon?: React.ReactNode;
  autoOpenPicker?: T extends "date" ? boolean : never;
};

const AutoOpenPickerWrapper = ({
  ref,
  children,
}: { children: React.ReactElement } & {
  ref?: React.Ref<HTMLInputElement>;
}) => {
  const internalRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (internalRef.current) {
      // Small delay to ensure the input is rendered and focused
      const timer = setTimeout(() => {
        if (internalRef.current) {
          internalRef.current.showPicker();
        }
      }, 2);

      return () => clearTimeout(timer);
    }
  }, []);

  return (
    <div
      data-slot="auto-open-picker-wrapper"
      ref={(node) => {
        if (node) {
          const input = node.querySelector("input") as HTMLInputElement;
          if (input) {
            internalRef.current = input;
            if (typeof ref === "function") {
              ref(input);
            } else if (ref) {
              ref.current = input;
            }
          }
        }
      }}
    >
      {children}
    </div>
  );
};
AutoOpenPickerWrapper.displayName = "AutoOpenPickerWrapper";

const Input = ({
  ref,
  className,
  type,
  icon,
  autoOpenPicker,
  ...props
}: InputProps & { ref?: React.Ref<HTMLInputElement> }) => {
  const dateCalendarAdjustment =
    type === "date"
      ? "relative [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:right-3 [&::-webkit-calendar-picker-indicator]:top-1/2 [&::-webkit-calendar-picker-indicator]:-translate-y-1/2"
      : "";

  const component = (
    <div className="relative w-full">
      <input
        data-slot="input"
        type={type}
        className={cn(
          "flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-xs transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
          icon && "pr-10 -mr-7",
          className,
          dateCalendarAdjustment,
        )}
        ref={ref}
        {...props}
      />
      {icon}
    </div>
  );

  if (type === "date" && autoOpenPicker) {
    return <AutoOpenPickerWrapper ref={ref}>{component}</AutoOpenPickerWrapper>;
  }

  return component;
};
Input.displayName = "Input";

interface OverridableInputProps extends InputProps {
  isOverridden?: boolean;
  onReset?: () => void;
}

const OverridableInput = ({
  ref,
  isOverridden,
  onReset,
  ...props
}: OverridableInputProps & { ref?: React.Ref<HTMLInputElement> }) => {
  const icon = isOverridden && onReset && (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 bg-blue-100 hover:bg-blue-300 rounded-md"
            onClick={onReset}
          >
            <Undo2 className="size-4 text-gray-500" />
          </button>
        </TooltipTrigger>
        <TooltipContent>
          This calculated field was manually updated
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );

  return (
    <Input data-slot="overridable-input" icon={icon} {...props} ref={ref} />
  );
};
OverridableInput.displayName = "Input";

interface NoEditOnlyClearableInputProps extends InputProps {
  onClear?: () => void;
}

// NOTE(matt): This was used to display a masked SSN.
const NoEditOnlyClearableInput = ({
  ref,
  onClear,
  ...props
}: NoEditOnlyClearableInputProps & { ref?: React.Ref<HTMLInputElement> }) => {
  const icon = onClear && (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-slate-100 rounded-md"
            onClick={onClear}
          >
            <Delete className="size-4" />
          </button>
        </TooltipTrigger>
        <TooltipContent>
          To edit, you must clear and re-enter the entire number.
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );

  return (
    <Input
      data-slot="no-edit-only-clearable-input"
      icon={icon}
      {...props}
      ref={ref}
      disabled
    />
  );
};
NoEditOnlyClearableInput.displayName = "Input";

export { Input, NoEditOnlyClearableInput, OverridableInput };
